import { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Text } from '@mantine/core';
import { useHover } from '@mantine/hooks';

interface SquareIndicatorProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  to?: string;
  onClick?: () => void;
  testId?: string;
}

const baseIconStyle: CSSProperties = {
  fontFamily: "'PT sans', sans-serif",
  height: 90,
  width: 100,
  padding: '12px 8px',
  borderRadius: 6,
  border: '1px solid var(--anti-flash-white)',
  lineHeight: 1.4,
  fontSize: 13,
  color: 'var(--black)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const baseIconHoverStyle: CSSProperties = {
  backgroundColor: 'var(--third)',
  border: '1px solid var(--yellow)',
};

export default function SquareIndicator({
  icon,
  label,
  value,
  to,
  onClick,
  testId,
}: SquareIndicatorProps) {
  const { hovered, ref } = useHover<HTMLDivElement>();

  const baseIcon = (
    <Stack
      ref={ref}
      align="center"
      justify="center"
      gap={2}
      style={{ ...baseIconStyle, ...(hovered ? baseIconHoverStyle : {}) }}
    >
      {icon}
      <Text span inherit>
        {label}
      </Text>
      <Text span fw={700} inherit>
        {value}
      </Text>
    </Stack>
  );

  if (!to) {
    return baseIcon;
  }

  return (
    <Link
      style={{ display: 'contents', color: 'inherit' }}
      to={to}
      target="_blank"
      onClick={onClick}
      data-testid={testId}
    >
      {baseIcon}
    </Link>
  );
}
